import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { masterJobsReadApi } from "../config/masterJobsApi";
import {
  buildJobListSummary,
  buildJobListTitle,
  extractJobLastDate,
  formatDisplayDate,
  getJobSlug,
  normalizeJobImportantDates,
} from "../config/publicPageContent";

const DEFAULT_FAQ = [
  {
    question: "How should I use these updates?",
    answer:
      "Use this section to discover recent notices quickly, then open the full post and verify every important detail from the official source before taking action.",
  },
  {
    question: "Why should I verify information on the official website?",
    answer:
      "Exam dates, eligibility, fees, links, and instructions can change. The official website should always be treated as the final authority.",
  },
  {
    question: "How often should I check this page?",
    answer:
      "Students and aspirants usually benefit from checking important sections daily or several times a week, especially near application, exam, or result deadlines.",
  },
];

const FAQ_BY_LABEL = {
  "latest-jobs": [
    {
      question: "What should I check before applying for a government job?",
      answer:
        "Always review eligibility, age limit, application fees, important dates, required documents, and the official notification before starting the form.",
    },
    {
      question: "Can I rely only on this jobs list?",
      answer:
        "This list is helpful for discovery, but you should still read the official recruitment notice because departments may update vacancies, dates, or rules.",
    },
    {
      question: "How can this section help serious aspirants?",
      answer:
        "It saves time during the search stage so candidates can spend more effort on preparation, document readiness, and application planning.",
    },
  ],
  "admit-cards": [
    {
      question: "What should I verify on my admit card?",
      answer:
        "Check your name, roll number, exam date, reporting time, exam center, and instructions carefully as soon as the admit card is available.",
    },
    {
      question: "Why is the admit card page useful?",
      answer:
        "It helps candidates notice release updates faster, especially when multiple exams are being tracked at the same time.",
    },
    {
      question: "What should I do after downloading an admit card?",
      answer:
        "Read the instructions fully, confirm allowed documents, and plan travel or reporting time well before exam day.",
    },
  ],
  "latest-results": [
    {
      question: "What should I look for after a result is published?",
      answer:
        "Check whether the update includes a scorecard, merit list, cutoff, selection list, or instructions for the next stage.",
    },
    {
      question: "Why is this results section important?",
      answer:
        "It helps candidates track multiple result announcements from one place without searching each board website separately every day.",
    },
    {
      question: "Should I save my result copy?",
      answer:
        "Yes. Keep a copy of the result or scorecard because it may be useful later during counseling, document verification, or future reference.",
    },
  ],
  admission: [
    {
      question: "How can this admission section help students?",
      answer:
        "It helps students track new admission forms, entrance notices, and application windows in a more organized way.",
    },
    {
      question: "What should I verify before applying for admission?",
      answer:
        "Students should check eligibility, fee details, required documents, application dates, counseling process, and official instructions carefully.",
    },
    {
      question: "Why should I not depend only on summary pages?",
      answer:
        "Admission rules may differ across institutions, and official brochures often include important conditions that short summaries cannot fully replace.",
    },
  ],
  "answer-keys": [
    {
      question: "How does an answer key help after the exam?",
      answer:
        "It helps candidates estimate performance, identify mistakes, and decide whether to prepare for the next stage or review weak topics.",
    },
    {
      question: "What else should I check with an answer key?",
      answer:
        "Look for objection windows, challenge instructions, and any final key updates announced by the exam authority.",
    },
    {
      question: "Can answer keys improve preparation?",
      answer:
        "Yes. Reviewing answer keys carefully helps students understand common errors and improve accuracy in future exams.",
    },
  ],
  job: [
    {
      question: "Why is the syllabus section important for preparation?",
      answer:
        "A syllabus gives structure to preparation by showing what topics are included, which areas matter most, and how to plan revision properly.",
    },
    {
      question: "How should I use a syllabus page effectively?",
      answer:
        "Start with the official topics, divide them into smaller study blocks, and combine syllabus reading with quizzes or practice questions.",
    },
    {
      question: "Should I revisit the syllabus during preparation?",
      answer:
        "Yes. Revisiting the syllabus regularly helps students stay aligned with the exam pattern and avoid wasting time on irrelevant topics.",
    },
  ],
};

const getFaqEntries = (label) => FAQ_BY_LABEL[String(label || "").trim()] || DEFAULT_FAQ;

const buildItemDetailPath = (label, job) => {
  const slug = getJobSlug(job);
  if (!slug) return "/job";
  return `/job/${slug}`;
};

function GenerateHomePageLinks({
  label,
  header,
  searchTerm,
  showFaq = true,
  showAllItems = false,
  previewCount = 20,
  viewMorePath = "",
  itemVariant = "compact",
  layoutVariant = "home",
}) {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const faqEntries = getFaqEntries(label);
  const resolvedItemVariant = itemVariant === "compact" && showAllItems ? "detailed" : itemVariant;

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchJobs = async () => {
      try {
        const data = await masterJobsReadApi.getLatest(label);
        if (signal.aborted) return;
        setJobs(data);
      } catch (err) {
        if (err.name !== "AbortError") {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
    return () => controller.abort();
  }, [label]);

  const filteredJobs = jobs.filter((job) =>
    job.title.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const visibleJobs = useMemo(() => {
    if (showAllItems) return filteredJobs;
    return filteredJobs.slice(0, Math.max(1, Number(previewCount) || 20));
  }, [filteredJobs, previewCount, showAllItems]);
  const shouldShowViewMore =
    !showAllItems && filteredJobs.length > visibleJobs.length && Boolean(viewMorePath);
  const cardClassName =
    layoutVariant === "page" ? "card card-expanded" : "card";
  const faqClassName =
    layoutVariant === "page" ? "section-faq-shell section-faq-expanded" : "section-faq-shell";

  return (
    <>
      <div className={cardClassName}>
        <h3 className="categoryheader">{header}</h3>

        {loading ? <p>Loading...</p> : null}
        {error ? <p style={{ color: "red" }}>Warning: {error}</p> : null}

        {!loading && !error ? (
          <>
            <ul id="jobsList">
              {visibleJobs.map((job, index) => {
                const importantDateEntries = normalizeJobImportantDates(job?.importantDates);
                const lastDate = extractJobLastDate(job);
                const summaryText = buildJobListSummary(job);
                const displayTitle = buildJobListTitle(job, label);
                const cleanedSummaryText =
                  layoutVariant === "page"
                    ? summaryText
                        .replace(` Last Date: ${lastDate}.`, ".")
                        .replace(`Last Date: ${lastDate}.`, "")
                        .replace(/\s*Last Date:\s*[^.]+\./i, ".")
                        .replace(/\s+\./g, ".")
                        .trim()
                    : summaryText;

                return (
                  <li
                    key={job.id || index}
                    className={resolvedItemVariant === "detailed" ? "job-list-item-detailed" : ""}
                  >
                    {layoutVariant === "page" ? (
                      <div className="job-list-item-page-header">
                        <Link
                          to={buildItemDetailPath(label, job)}
                          state={{ type: label === "doc" ? "syllabus-doc" : "jobs" }}
                        >
                          <span className="job-list-item-title-text">{displayTitle.title}</span>
                          {displayTitle.dateLabel ? (
                            <span className="job-list-item-title-date">
                              {" "}
                              - {displayTitle.dateLabel}
                            </span>
                          ) : null}
                        </Link>
                      </div>
                    ) : (
                      <>
                        <Link
                          to={buildItemDetailPath(label, job)}
                          state={{ type: label === "doc" ? "syllabus-doc" : "jobs" }}
                        >
                          <span className="job-list-item-title-text">{displayTitle.title}</span>
                          {displayTitle.dateLabel ? (
                            <span className="job-list-item-title-date">
                              {" "}
                              - {displayTitle.dateLabel}
                            </span>
                          ) : null}
                        </Link>
                      </>
                    )}
                    {resolvedItemVariant === "detailed" ? (
                      <div className="job-list-item-meta">
                        {job?.postDate && layoutVariant !== "page" ? (
                          <p className="job-list-item-date">{formatDisplayDate(job.postDate)}</p>
                        ) : null}
                        {importantDateEntries.length ? (
                          <details className="job-list-item-dates-toggle">
                            <summary className="job-list-item-dates-summary">
                              <span className="job-list-item-dates-trigger">
                                <span className="job-list-item-dates-label">
                                  Important Dates
                                </span>
                                <span className="job-list-item-dates-plus" aria-hidden="true">
                                  +
                                </span>
                                <span className="job-list-item-dates-count">
                                  {importantDateEntries.length}
                                </span>
                              </span>
                            </summary>
                            <dl className="job-list-item-dates">
                              {importantDateEntries.map((item) => (
                                <div
                                  key={`${job.id || index}-${item.key}`}
                                  className="job-list-item-date-row"
                                >
                                  <dt>{item.label}</dt>
                                  <dd>{item.value}</dd>
                                </div>
                              ))}
                            </dl>
                          </details>
                        ) : null}
                        <p className="job-list-item-summary">{cleanedSummaryText}</p>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>

            {shouldShowViewMore ? (
              <button className="view-more-btn" onClick={() => navigate(viewMorePath)}>
                View More
              </button>
            ) : null}
          </>
        ) : null}
      </div>

      {showFaq && !loading && !error ? (
        <div className={faqClassName} aria-label={`${header} frequently asked questions`}>
          <h4 className="section-faq-title">{`${header} FAQ`}</h4>
          <div className="section-faq-list">
            {faqEntries.map((item) => (
              <section key={item.question} className="section-faq-item">
                <h5>{item.question}</h5>
                <p>{item.answer}</p>
              </section>
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
}

export default GenerateHomePageLinks;
